// Expense queries and mutations (slice 4). Every mutation refreshes all finance data and toasts. All but the keep and
// its undo settle on the server's answer, so a modal closes on the 200 while the lists refresh behind it.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { formatMoney } from "@/utils/currency";
import { expenseService } from "../services/expenseService";
import type { ExpenseCategory, ExpensePaidInput, ExpenseTemplate, ExpenseTemplatesResponse, NewTemplateInput, RecordExpenseInput, SetBillPassInput, SetPotInput, UpdateTemplateInput } from "../types";
import { addedToast, deletedToast, replaceTemplate, stopToast } from "../utils/expenses";
import { dateLabel, monthLabel } from "../utils/format";
import { retryUnlessMissing } from "../utils/jobForms";
import { financeKeys, useFinanceMutation } from "./financeQuery";

export const expenseKeys = {
  categories: () => [...financeKeys.all, "expense-categories"] as const,
  templates: () => [...financeKeys.all, "expense-templates"] as const,
  entry: (id: string) => [...financeKeys.all, "expense", id] as const,
};

const ON_RESPONSE = { settleOnResponse: true };

export const useExpenseCategories = () => useQuery({ queryKey: expenseKeys.categories(), queryFn: expenseService.categories });
export const useExpenseTemplates = () => useQuery({ queryKey: expenseKeys.templates(), queryFn: expenseService.templates });
/** The agency's pots, for a pick (slice 9 Part B, R18): the categories marked pot, empty while they load and when none is made. */
export function usePots(): ExpenseCategory[] {
  return (useExpenseTemplates().data?.categories ?? []).filter((c) => c.pot);
}
export const useExpenseEntry = (id: string) =>
  useQuery({ queryKey: expenseKeys.entry(id), queryFn: () => expenseService.entry(id), retry: retryUnlessMissing });

export const useAddTemplate = () => useFinanceMutation((input: NewTemplateInput) => expenseService.addTemplate(input), addedToast, ON_RESPONSE);

/** Writes the template the server answered into the list, so a modal reopened before the refetch lands reads it. */
function useTemplateCache() {
  const queryClient = useQueryClient();
  return (t: ExpenseTemplate): ExpenseTemplate => {
    queryClient.setQueryData<ExpenseTemplatesResponse>(expenseKeys.templates(), (list) => (list ? replaceTemplate(list, t) : list));
    return t;
  };
}

export function useUpdateTemplate() {
  const cache = useTemplateCache();
  return useFinanceMutation(
    (v: { id: string; input: UpdateTemplateInput }) => expenseService.updateTemplate(v.id, v.input).then(cache),
    (t) => `${t.label} saved.`,
    ON_RESPONSE,
  );
}

export function useStopTemplate() {
  const cache = useTemplateCache();
  return useFinanceMutation((id: string) => expenseService.stopTemplate(id).then(cache), stopToast, ON_RESPONSE);
}
/** Every finance query refetches after it, the templates, Money and the slices among them. */
export const useDeleteTemplate = () => useFinanceMutation((id: string) => expenseService.deleteTemplate(id), deletedToast, ON_RESPONSE);
/** A category named on its own, from Setup's panel (finance-real round 3), a pot or not (round 8). A name taken in any case is refused. */
export const useCreateCategory = () =>
  useFinanceMutation((v: { label: string; pot: boolean }) => expenseService.createCategory(v.label, v.pot), (c) => `${c.label} added${c.pot ? " as a pot" : ""}.`, ON_RESPONSE);
/** A category's name and whether it is a pot, saved together from its drawer (finance-real round 8, R2). */
export const useUpdateCategory = () =>
  useFinanceMutation(
    (v: { id: string; label: string; pot: boolean }) => expenseService.updateCategory(v.id, { label: v.label, pot: v.pot }),
    (c) => `${c.label} saved${c.pot ? ", a pot" : ""}.`,
    ON_RESPONSE,
  );
export const useDeleteCategory = () => useFinanceMutation((id: string) => expenseService.deleteCategory(id), deletedToast, ON_RESPONSE);
export const useRecordExpense = () =>
  useFinanceMutation(
    (v: { input: RecordExpenseInput; requestId: string }) => expenseService.record(v.input, v.requestId),
    (e) => `${e.label} recorded, paid ${dateLabel(e.paidOn)}.`,
    ON_RESPONSE,
  );

/** Marks a bill paid; the answer seeds the bill's own query so its modal reads it at once. */
export function useMarkExpensePaid() {
  const queryClient = useQueryClient();
  return useFinanceMutation(
    (v: { id: string; input: ExpensePaidInput; requestId: string }) =>
      expenseService.markPaid(v.id, v.input, v.requestId).then((entry) => {
        queryClient.setQueryData(expenseKeys.entry(entry.id), entry);
        return entry;
      }),
    (e) => `${e.label} paid ${dateLabel(e.paidOn)}.`,
    ON_RESPONSE,
  );
}

/** Names the pot a paid cost comes out of (finance-real round 8). The answer seeds the bill's own query. */
export function useSetBillPot() {
  const queryClient = useQueryClient();
  return useFinanceMutation(
    (v: { id: string; input: SetPotInput }) =>
      expenseService.setPot(v.id, v.input).then((entry) => {
        queryClient.setQueryData(expenseKeys.entry(entry.id), entry);
        return entry;
      }),
    (e) => (e.pot ? `${e.label} comes out of ${e.pot.label}.` : `${e.label} comes out of no pot.`),
    ON_RESPONSE,
  );
}

/**
 * Keeps what a pot's month has not assigned as BeNerdy's own (slice 12, port Q6). The server works out how much. It
 * settles after the refetch, since the pot month's drawer stays open and reads the fresh month next, so it never offers
 * the old keep after one that worked.
 */
export const useKeepOwn = () =>
  useFinanceMutation(
    (v: { potId: string; month: string }) => expenseService.keepOwn(v.potId, v.month),
    (k) => `${monthLabel(k.month)}: ${formatMoney(k.keptMinor, "EUR")} kept as BeNerdy's own.`,
  );
/** Undoes a month's keep: what it kept reads not assigned again. It settles after the refetch too, so the kept row is gone when the drawer's buttons come back. */
export const useUnkeepOwn = () =>
  useFinanceMutation((v: { potId: string; month: string }) => expenseService.unkeepOwn(v.potId, v.month), (k) => `${monthLabel(k.month)} keeps nothing as BeNerdy's own.`);

/** Re-points a bill passed along; the answer seeds the bill's own query so its modal reads it at once. */
export function useSetBillPass() {
  const queryClient = useQueryClient();
  return useFinanceMutation(
    (v: { id: string; input: SetBillPassInput }) =>
      expenseService.setPass(v.id, v.input).then((entry) => {
        queryClient.setQueryData(expenseKeys.entry(entry.id), entry);
        return entry;
      }),
    (e) => `${e.label} now rides ${e.passAlong?.contractName ?? `its ${ENTITY_CONFIG.contract.label.toLowerCase()}`}.`,
    ON_RESPONSE,
  );
}
